import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useHoverIntent } from '@/hooks/useHoverIntent';
import { cn } from '@/lib/utils';
import type { Identity } from '@/types/identity';
import { getIdentityFallbackIcon, getIdentityLabel } from '@/utils/identity';
import {
    EntityActionsMenu,
    type ActionMenuItem,
} from '@/components/EntityActionsMenu/EntityActionsMenu';
import { EntityAvatar } from '@/components/EntityAvatar/EntityAvatar';
import {
    PopoverAnchor,
    PopoverPositioner,
    PopoverRoot,
} from '@/components/Popover';
import { POPOVER_SURFACE_CLASSES } from '@/components/ui/surface';
import { SingleLineTagRow } from './SingleLineTagRow';

interface IdentityHoverCardProps {
    identity: Identity;
    menuItems: ActionMenuItem[];
    children: ReactNode;
}

export function IdentityHoverCard({
    identity,
    menuItems,
    children,
}: IdentityHoverCardProps) {
    const { t } = useTranslation();
    const { isOpen, setIsOpen, handlePointerEnter, handlePointerLeave } =
        useHoverIntent();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const anchor = useRef<HTMLDivElement>(null);
    const isPointerOverCard = useRef(false);

    const handleCardPointerEnter = () => {
        isPointerOverCard.current = true;
        handlePointerEnter();
    };
    const handleCardPointerLeave = () => {
        isPointerOverCard.current = false;
        if (!isMenuOpen) {
            handlePointerLeave();
        }
    };
    const closeUnlessPointerOverCard = () => {
        if (!isPointerOverCard.current) {
            handlePointerLeave();
        }
    };
    const keepOpenWhileInteractingWithCard = (
        event: CustomEvent<{ originalEvent: Event }>,
    ) => {
        const isAnchorInteraction = anchor.current?.contains(
            event.detail.originalEvent.target as Node,
        );
        if (isMenuOpen || isAnchorInteraction) {
            event.preventDefault();
        }
    };
    const FallbackIcon = getIdentityFallbackIcon(identity.kind);

    return (
        <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
            <PopoverAnchor asChild>
                <div
                    ref={anchor}
                    onPointerEnter={handleCardPointerEnter}
                    onPointerLeave={handleCardPointerLeave}
                >
                    {children}
                </div>
            </PopoverAnchor>
            <PopoverPositioner
                side="top"
                sideOffset={8}
                align="center"
                collisionPadding={16}
                onPointerEnter={handleCardPointerEnter}
                onPointerLeave={handleCardPointerLeave}
                onOpenAutoFocus={(event) => event.preventDefault()}
                onFocusOutside={keepOpenWhileInteractingWithCard}
                onInteractOutside={keepOpenWhileInteractingWithCard}
                className="overflow-y-visible"
            >
                <div
                    className={cn(
                        POPOVER_SURFACE_CLASSES,
                        'flex w-[367px] flex-col items-start p-4 shadow-floating-card',
                    )}
                >
                    <div className="relative flex w-full items-center gap-3">
                        <EntityAvatar
                            icon={<FallbackIcon weight="regular" />}
                            imageUrl={identity.thumbnailUrl}
                            shape="circle"
                            size="md"
                            className="size-16"
                        />
                        <div
                            className={cn(
                                'flex min-w-0 flex-1 flex-col items-start justify-center gap-2.5',
                                identity.known
                                    ? 'text-white-100'
                                    : 'text-grey-200',
                            )}
                        >
                            <span className="max-w-full truncate pr-8 text-md-medium">
                                {getIdentityLabel(t, identity)}
                            </span>
                            {identity.tags.length > 0 && (
                                <SingleLineTagRow tags={identity.tags} />
                            )}
                        </div>
                        <EntityActionsMenu
                            items={menuItems}
                            triggerIconOrientation="horizontal"
                            triggerAriaLabel={t('actions.more')}
                            wrapperClassName="absolute right-0 top-0"
                            triggerButtonClassName="p-0"
                            triggerIconClassName="size-6 text-grey-200 hover:text-white-100 transition-colors"
                            onOpenChange={(open) => {
                                setIsMenuOpen(open);
                                if (!open) {
                                    closeUnlessPointerOverCard();
                                }
                            }}
                        />
                    </div>
                </div>
            </PopoverPositioner>
        </PopoverRoot>
    );
}
